import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonPage,
  IonTitle,
  IonToolbar,
  useIonViewWillEnter,
} from '@ionic/react'
import React, { BaseSyntheticEvent, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { chevronBackOutline } from 'ionicons/icons'
import {
  setUpdateInputRows,
  saveVentricularAssistDevice,
  setIndication,
  setAuscultated,
  setFlowType,
  setInflowCannulaSiteAssessment,
  setOutflowCannulaSiteAssessment,
  setPulsatilityIndex,
  setVentricularAssistDeviceType,
  setSpeedRpms,
  setAnticoagulation,
  setAntidysrhythmia,
  setFlowLMin,
  setPowerWatts,
  setBatteryStatus,
  setBackupOptions,
  setSiteAssessment,
  setPLevel,
  setControllerNumber,
  setMotorCurrentMa,
  setMotorCurrentWaveformCharacteristics,
  setPlacementConfirmedBy,
  setCatheterSize,
  setCatheterDepthCm,
  setCatheterSerialNumber,
  setInsertLocation,
  setPlacementLocation,
  setLegImmobilized,
  setCatheterSuturedInPlace,
  setDressingCondition,
  setPulsesDistalToInsertionSite,
  setAnchoringRingLocked,
  setHeadOfBedAt30Degrees,
  setPurgeSolutionDextrosePercent,
  setPurgeSolutionHeparinIuMl,
  setPurgeSolutionFlowMlHr,
  setPressureBagUsed,
  setPurgePressureMmHg,
  setAlarms,
  setVadCoordinatorNotification,
  setPlacementSignalMmHg,
  setPlacementSignalWaveformCharacteristics,
  setPowerType,
  setMakeModelImpella,
  setMakeModelVad,
} from '../store/ventricularAssistDevice.actions'
import { getCurrentDate, getCurrentTime, randomNumber } from '../../../../utilities'
import { ProcedureConstants } from '../../../../components/procedure/store/procedure.constants'
import { VentricularAssistDeviceConstants } from '../store/ventricularAssistDevice.constants'
import { TabConstants } from '../../../../components/tabs/Tabs.constants'
import { setInputRows } from '../../../../components/procedure/store/procedure.actions'
import { getVentricularAssistDeviceSelector } from '../selectors'
import { getVentricularAssistDeviceOptionsSelector } from '../../../options/selectors'
import PCRHeader from '../../../../components/pcrHeader/PCRHeader.component'
import Procedure from '../../../../components/procedure/Procedure.component'
import { showAlert } from '../../../../components/alert/store/alert.actions'
import { compareObjectsIfEqual, filterOutObjectKeys } from '../../../../utilities/object.util'
import { initialVentricularAssistDeviceState } from '../store/ventricularAssistDevice.reducers'
import { setDeleteProcedure, setProcedureLoaderItem } from '../../../timeline/store/timeline.actions'
import { TimelineConstants } from '../../../timeline/store/timeline.constants'
import { goBack } from '../../../../utilities/navigation.util'
import { IOption } from '../../../../common/common.types'
import { getGenericOptionsSelector } from '../../../options/selectors/genericOptions.selector'
import { numberPadKeysWithDecimal } from '../../../../components/numberPad/NumberPad.types'
import { IServiceConfig } from '../../../configurations/store/configurations.types'
import { getServiceConfigSelector } from '../../../configurations/selectors/serviceConfig.selector'
import { IAlertState } from '../../../../components/alert/store/alert.types'

type ActionCreator<T> = (payload: T) => { type: string; payload: T }

const VentricularAssistDevice: React.FC = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const history = useHistory()
  const ventricularAssistDevice = useSelector(getVentricularAssistDeviceSelector)
  const {
    date,
    time,
    pta,
    assessedBy,
    rolePerforming,
    rolePerformingPNNV,
    successfulAttempt,
    procedureComplications,
    response,
    responsePNNV,
    authorization,
    comments,
    updateInputRows,
    isEkgImport,
    ventricularAssistDeviceType,
    indication,
    pulsatilityIndex,
    auscultated,
    makeModelVad,
    makeModelImpella,
    pLevel,
    speedRpms,
    flowType,
    controllerNumber,
    motorCurrentMa,
    motorCurrentWaveformCharacteristics,
    placementSignalMmHg,
    placementSignalWaveformCharacteristics,
    placementConfirmedBy,
    catheterSize,
    catheterDepthCm,
    catheterSerialNumber,
    insertLocation,
    placementLocation,
    siteAssessment,
    legImmobilized,
    catheterSuturedInPlace,
    dressingCondition,
    pulsesDistalToInsertionSite,
    anchoringRingLocked,
    headOfBedAt30Degrees,
    anticoagulation,
    antidysrhythmia,
    purgeSolutionDextrosePercent,
    purgeSolutionHeparinIuMl,
    purgeSolutionFlowMlHr,
    pressureBagUsed,
    purgePressureMmHg,
    flowLMin,
    powerWatts,
    powerType,
    batteryStatus,
    backupOptions,
    alarms,
    vadCoordinatorNotification,
    inflowCannulaSiteAssessment,
    outflowCannulaSiteAssessment,
    _hideVadFields,
    _hideImpellaFields,
    procedurePerformedByAnotherUnit,
    procedurePerformedByAnotherUnitPNNV,
  } = ventricularAssistDevice
  const ventricularAssistDeviceOptions = useSelector(getVentricularAssistDeviceOptionsSelector)
  const {
    vadTypeOptions,
    vadAlarmsOptions,
    vadSiteAssessmentOptions,
    vadCatheterSizeOptions,
    vadPlacementConfirmedByOptions,
    vadInsertLocationOptions,
    vadMotorCurrentWaveformCharacteristicsOptions,
    vadPlacementSignalWaveformCharacteristicsOptions,
    vadPlacementLocationOptions,
    vadPowerTypeOptions,
    vadBackupOptions,
    vadBatteryStatusOptions,
    vadFlowTypeOptions,
    vadIndicationOptions,
    vadMakeModelOptions,
    vadMakeModelImpellaOptions,
    vadPLevelOptions,
  } = ventricularAssistDeviceOptions

  const genericOptions = useSelector(getGenericOptionsSelector)
  const { yesNoOptions } = genericOptions

  const serviceConfigData: IServiceConfig = useSelector(getServiceConfigSelector)
  const { DISPLAY_SECONDS } = serviceConfigData
  const displaySeconds = !!DISPLAY_SECONDS

  const dateTimeValidationCheck = () => {
    if (!date || !time) {
      dispatch(showAlert({ header: '', message: 'Date / Time is required' }))
      return false
    }
    return true
  }

  const initialStateCheck = () => {
    if (pta && ((date && time) || (!date && !time))) return true
    const fieldsToFilter = ['key', 'addToTimeline', 'updateInputRows', 'nemsisCode']
    const currentState = filterOutObjectKeys(ventricularAssistDevice, fieldsToFilter)
    const initalState = filterOutObjectKeys(initialVentricularAssistDeviceState, fieldsToFilter)
    if (!compareObjectsIfEqual(currentState, initalState)) {
      return dateTimeValidationCheck()
    }
    return true
  }

  const onSaveClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    if (!pta && (!date || !time)) {
      dateTimeValidationCheck()
      return
    }
    const dateTimeValidationCheckResult = initialStateCheck()
    if (dateTimeValidationCheckResult) {
      history.push(TabConstants.TIMELINE_TAB)
    }
  }

  const onBackClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    if ((!date || !time) && !pta) {
      const alert: IAlertState = {
        message: t('common.saveTimelineEntry'),
        buttons: [
          {
            text: t('common.buttons.Yes'),
            handler: () => {
              const date = getCurrentDate()
              let time = getCurrentTime()
              if (!displaySeconds) {
                time = time.replace(/(:\d+)(:\d+)$/, '$1:00')
              }

              dispatch({ type: 'ventricularAssistDevice/SET_TIME', time: time })
              dispatch({ type: 'ventricularAssistDevice/SET_DATE', date: date })
              dispatch(saveVentricularAssistDevice())

              goBack()
            },
          },
          {
            text: t('common.buttons.No'),
            role: 'destructive',
            handler: () => {
              const itemToRemove = {
                key: ventricularAssistDevice.key,
                rowActionType: TimelineConstants.SET_VENTRICULAR_ASSIST_DEVICE,
              }
              dispatch(setDeleteProcedure(true))
              dispatch({ type: TimelineConstants.DELETE_TIMELINE_ITEM, payload: itemToRemove })
              goBack()
            },
          },
        ],
      }
      dispatch(showAlert(alert))
      dispatch(setProcedureLoaderItem(false))
    } else {
      const dateTimeValidationCheck = initialStateCheck()
      if (dateTimeValidationCheck) {
        goBack()
      }
      dispatch(setProcedureLoaderItem(false))
    }
  }

  // single select change function creator
  const createChangeHandler = useCallback(
    (action: ActionCreator<IOption | null>) => (value: IOption | null) => {
      dispatch(action(value))
      dispatch(saveVentricularAssistDevice())
    },
    [dispatch]
  )

  // number field change function creator
  const createNumberPadHandler = useCallback(
    (action: ActionCreator<string>) => (values: string[]) => {
      dispatch(action(values.join('')))
      dispatch(saveVentricularAssistDevice())
    },
    [dispatch]
  )

  // text field change function creator
  const createTextHandler = useCallback(
    (action: ActionCreator<string>) => (e: CustomEvent) => {
      e.preventDefault()
      dispatch(action(e.detail.value))
      dispatch(saveVentricularAssistDevice())
    },
    [dispatch]
  )

  const setVentricularAssistDeviceTypeOnChange = (value: IOption | null) => {
    dispatch(setVentricularAssistDeviceType(value))
  }

  const inputRows = [
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: VentricularAssistDeviceConstants.SET_VENTRICULAR_ASSIST_DEVICE_TYPE,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: false,
          props: {
            label: t('ventricularAssistDevice.Type'),
            dataTestID: 'Type',
            listHeader: t('ventricularAssistDevice.Type'),
            options: vadTypeOptions,
            action: setVentricularAssistDeviceTypeOnChange,
            selectedOption: ventricularAssistDeviceType,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          type: 'separator',
          hidden: _hideImpellaFields && _hideVadFields,
          props: {
            heading:
              ventricularAssistDeviceType?.id !== 4
                ? t('ventricularAssistDevice.VAD')
                : t('ventricularAssistDevice.Impella'),
          },
        },
      ],
    },
    // VAD Type fields
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: VentricularAssistDeviceConstants.SET_INDICATION,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: _hideVadFields,
          props: {
            label: t('ventricularAssistDevice.Indication'),
            dataTestID: 'Indication',
            listHeader: t('ventricularAssistDevice.Indication'),
            options: vadIndicationOptions,
            action: createChangeHandler(setIndication),
            selectedOption: indication,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: VentricularAssistDeviceConstants.SET_PULSATILITY_INDEX,
          type: ProcedureConstants.NUMBER_PAD,
          hidden: _hideVadFields,
          props: {
            label: t('ventricularAssistDevice.PulsatilityIndex'),
            dataTestId: 'PulsatilityIndex',
            elementId: 'PulsatilityIndex',
            values: pulsatilityIndex?.split('') || [],
            onNumPadClick: createNumberPadHandler(setPulsatilityIndex),
            validCharacters: numberPadKeysWithDecimal,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: VentricularAssistDeviceConstants.SET_AUSCULTATED,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: _hideVadFields,
          props: {
            label: t('ventricularAssistDevice.Auscultated'),
            dataTestID: 'Auscultated',
            listHeader: t('ventricularAssistDevice.Auscultated'),
            options: yesNoOptions,
            action: createChangeHandler(setAuscultated),
            selectedOption: auscultated,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: VentricularAssistDeviceConstants.SET_MAKE_MODEL_VAD,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: _hideVadFields,
          props: {
            label: t('ventricularAssistDevice.MakeModel'),
            dataTestID: 'MakeModelVad',
            listHeader: t('ventricularAssistDevice.MakeModel'),
            options: vadMakeModelOptions,
            action: createChangeHandler(setMakeModelVad),
            selectedOption: makeModelVad,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: VentricularAssistDeviceConstants.SET_SPEED_RPMS,
          type: ProcedureConstants.NUMBER_PAD,
          hidden: _hideVadFields,
          props: {
            label: t('ventricularAssistDevice.SpeedRPMs'),
            dataTestId: 'SpeedRPMs',
            elementId: 'SpeedRPMs',
            values: speedRpms?.split('') || [],
            onNumPadClick: createNumberPadHandler(setSpeedRpms),
            validCharacters: numberPadKeysWithDecimal,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: VentricularAssistDeviceConstants.SET_FLOW_TYPE,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: _hideVadFields,
          props: {
            label: t('ventricularAssistDevice.FlowType'),
            dataTestID: 'FlowType',
            listHeader: t('ventricularAssistDevice.FlowType'),
            options: vadFlowTypeOptions,
            action: createChangeHandler(setFlowType),
            selectedOption: flowType,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: VentricularAssistDeviceConstants.SET_INFLOW_CANNULA_SITE_ASSESSMENT,
          type: ProcedureConstants.TEXT,
          hidden: _hideVadFields,
          props: {
            label: t('ventricularAssistDevice.InflowCannulaSiteAssessment'),
            dataTestID: 'InflowCannulaSiteAssessment',
            onChangeAction: createTextHandler(setInflowCannulaSiteAssessment),
            inputValue: inflowCannulaSiteAssessment,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: VentricularAssistDeviceConstants.SET_OUTFLOW_CANNULA_SITE_ASSESSMENT,
          type: ProcedureConstants.TEXT,
          hidden: _hideVadFields,
          props: {
            label: t('ventricularAssistDevice.OutflowCannulaSiteAssessment'),
            dataTestID: 'OutflowCannulaSiteAssessment',
            onChangeAction: createTextHandler(setOutflowCannulaSiteAssessment),
            inputValue: outflowCannulaSiteAssessment,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: VentricularAssistDeviceConstants.SET_ANTICOAGULATION,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: _hideVadFields,
          props: {
            label: t('ventricularAssistDevice.Anticoagulation'),
            dataTestID: 'Anticoagulation',
            listHeader: t('ventricularAssistDevice.Anticoagulation'),
            options: yesNoOptions,
            action: createChangeHandler(setAnticoagulation),
            selectedOption: anticoagulation,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: VentricularAssistDeviceConstants.SET_ANTIDYSRHYTHMIA,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: _hideVadFields,
          props: {
            label: t('ventricularAssistDevice.Antidysrhythmia'),
            dataTestID: 'Antidysrhythmia',
            listHeader: t('ventricularAssistDevice.Antidysrhythmia'),
            options: yesNoOptions,
            action: createChangeHandler(setAntidysrhythmia),
            selectedOption: antidysrhythmia,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: VentricularAssistDeviceConstants.SET_FLOW_L_MIN,
          type: ProcedureConstants.NUMBER_PAD,
          hidden: _hideVadFields,
          props: {
            label: t('ventricularAssistDevice.FlowLMin'),
            dataTestId: 'FlowLMin',
            elementId: 'FlowLMin',
            values: flowLMin?.split('') || [],
            onNumPadClick: createNumberPadHandler(setFlowLMin),
            validCharacters: numberPadKeysWithDecimal,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: VentricularAssistDeviceConstants.SET_POWER_WATTS,
          type: ProcedureConstants.NUMBER_PAD,
          hidden: _hideVadFields,
          props: {
            label: t('ventricularAssistDevice.PowerWatts'),
            dataTestId: 'PowerWatts',
            elementId: 'PowerWatts',
            values: powerWatts?.split('') || [],
            onNumPadClick: createNumberPadHandler(setPowerWatts),
            validCharacters: numberPadKeysWithDecimal,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: VentricularAssistDeviceConstants.SET_BATTERY_STATUS,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: _hideVadFields,
          props: {
            label: t('ventricularAssistDevice.BatteryStatus'),
            dataTestID: 'BatteryStatus',
            listHeader: t('ventricularAssistDevice.BatteryStatus'),
            options: vadBatteryStatusOptions,
            action: createChangeHandler(setBatteryStatus),
            selectedOption: batteryStatus,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: VentricularAssistDeviceConstants.SET_BACKUP_OPTIONS,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: _hideVadFields,
          props: {
            label: t('ventricularAssistDevice.BackupOptions'),
            dataTestID: 'BackupOptions',
            listHeader: t('ventricularAssistDevice.BackupOptions'),
            options: vadBackupOptions,
            action: createChangeHandler(setBackupOptions),
            selectedOption: backupOptions,
          },
        },
      ],
    },
    // Impella Fields
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: VentricularAssistDeviceConstants.SET_INDICATION,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: _hideImpellaFields,
          props: {
            label: t('ventricularAssistDevice.Indication'),
            dataTestID: 'Indication',
            listHeader: t('ventricularAssistDevice.Indication'),
            options: vadIndicationOptions,
            action: createChangeHandler(setIndication),
            selectedOption: indication,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: VentricularAssistDeviceConstants.SET_PULSATILITY_INDEX,
          type: ProcedureConstants.NUMBER_PAD,
          hidden: _hideImpellaFields,
          props: {
            label: t('ventricularAssistDevice.PulsatilityIndex'),
            dataTestId: 'PulsatilityIndex',
            elementId: 'PulsatilityIndex',
            values: pulsatilityIndex?.split('') || [],
            onNumPadClick: createNumberPadHandler(setPulsatilityIndex),
            validCharacters: numberPadKeysWithDecimal,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: VentricularAssistDeviceConstants.SET_AUSCULTATED,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: _hideImpellaFields,
          props: {
            label: t('ventricularAssistDevice.Auscultated'),
            dataTestID: 'Auscultated',
            listHeader: t('ventricularAssistDevice.Auscultated'),
            options: yesNoOptions,
            action: createChangeHandler(setAuscultated),
            selectedOption: auscultated,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: VentricularAssistDeviceConstants.SET_MAKE_MODEL_IMPELLA,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: _hideImpellaFields,
          props: {
            label: t('ventricularAssistDevice.MakeModel'),
            dataTestID: 'MakeModelImpella',
            listHeader: t('ventricularAssistDevice.MakeModel'),
            options: vadMakeModelImpellaOptions,
            action: createChangeHandler(setMakeModelImpella),
            selectedOption: makeModelImpella,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: VentricularAssistDeviceConstants.SET_P_LEVEL,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: _hideImpellaFields,
          props: {
            label: t('ventricularAssistDevice.PLevel'),
            dataTestID: 'PLevel',
            listHeader: t('ventricularAssistDevice.PLevel'),
            options: vadPLevelOptions,
            action: createChangeHandler(setPLevel),
            selectedOption: pLevel,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: VentricularAssistDeviceConstants.SET_SPEED_RPMS,
          type: ProcedureConstants.NUMBER_PAD,
          hidden: _hideImpellaFields,
          props: {
            label: t('ventricularAssistDevice.SpeedRPMs'),
            dataTestId: 'SpeedRPMs',
            elementId: 'SpeedRPMs',
            values: speedRpms?.split('') || [],
            onNumPadClick: createNumberPadHandler(setSpeedRpms),
            validCharacters: numberPadKeysWithDecimal,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: VentricularAssistDeviceConstants.SET_FLOW_TYPE,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: _hideImpellaFields,
          props: {
            label: t('ventricularAssistDevice.FlowType'),
            dataTestID: 'FlowType',
            listHeader: t('ventricularAssistDevice.FlowType'),
            options: vadFlowTypeOptions,
            action: createChangeHandler(setFlowType),
            selectedOption: flowType,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: VentricularAssistDeviceConstants.SET_CONTROLLER_NUMBER,
          type: ProcedureConstants.NUMBER_PAD,
          hidden: _hideImpellaFields,
          props: {
            label: t('ventricularAssistDevice.ControllerNumber'),
            dataTestId: 'ControllerNumber',
            elementId: 'ControllerNumber',
            values: controllerNumber?.split('') || [],
            onNumPadClick: createNumberPadHandler(setControllerNumber),
            validCharacters: numberPadKeysWithDecimal,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: VentricularAssistDeviceConstants.SET_MOTOR_CURRENT_MA,
          type: ProcedureConstants.NUMBER_PAD,
          hidden: _hideImpellaFields,
          props: {
            label: t('ventricularAssistDevice.MotorCurrentMA'),
            dataTestId: 'MotorCurrentMA',
            elementId: 'MotorCurrentMA',
            values: motorCurrentMa?.split('') || [],
            onNumPadClick: createNumberPadHandler(setMotorCurrentMa),
            validCharacters: numberPadKeysWithDecimal,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: VentricularAssistDeviceConstants.SET_MOTOR_CURRENT_WAVE_FORM_CHARACTERISTICS,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: _hideImpellaFields,
          props: {
            label: t('ventricularAssistDevice.MotorCurrentWaveformCharacteristics'),
            dataTestID: 'MotorCurrentWaveformCharacteristics',
            listHeader: t('ventricularAssistDevice.MotorCurrentWaveformCharacteristics'),
            options: vadMotorCurrentWaveformCharacteristicsOptions,
            action: createChangeHandler(setMotorCurrentWaveformCharacteristics),
            selectedOption: motorCurrentWaveformCharacteristics,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: VentricularAssistDeviceConstants.SET_PLACEMENT_SIGNAL_MMHG,
          type: ProcedureConstants.NUMBER_PAD,
          hidden: _hideImpellaFields,
          props: {
            label: t('ventricularAssistDevice.PlacementSignalMmHg'),
            dataTestId: 'PlacementSignalMmHg',
            elementId: 'PlacementSignalMmHg',
            values: placementSignalMmHg?.split('') || [],
            onNumPadClick: createNumberPadHandler(setPlacementSignalMmHg),
            validCharacters: numberPadKeysWithDecimal,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: VentricularAssistDeviceConstants.SET_PLACEMENT_SIGNAL_WAVE_FORM_CHARACTERISTICS,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: _hideImpellaFields,
          props: {
            label: t('ventricularAssistDevice.PlacementSignalWaveformCharacteristics'),
            dataTestID: 'PlacementSignalWaveformCharacteristics',
            listHeader: t('ventricularAssistDevice.PlacementSignalWaveformCharacteristics'),
            options: vadPlacementSignalWaveformCharacteristicsOptions,
            action: createChangeHandler(setPlacementSignalWaveformCharacteristics),
            selectedOption: placementSignalWaveformCharacteristics,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: VentricularAssistDeviceConstants.SET_PLACEMENT_CONFIRMED_BY,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: _hideImpellaFields,
          props: {
            label: t('ventricularAssistDevice.PlacementConfirmedBy'),
            dataTestID: 'PlacementConfirmedBy',
            listHeader: t('ventricularAssistDevice.PlacementConfirmedBy'),
            options: vadPlacementConfirmedByOptions,
            action: createChangeHandler(setPlacementConfirmedBy),
            selectedOption: placementConfirmedBy,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: VentricularAssistDeviceConstants.SET_CATHETER_SIZE,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: _hideImpellaFields,
          props: {
            label: t('ventricularAssistDevice.CatheterSize'),
            dataTestID: 'CatheterSize',
            listHeader: t('ventricularAssistDevice.CatheterSize'),
            options: vadCatheterSizeOptions,
            action: createChangeHandler(setCatheterSize),
            selectedOption: catheterSize,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: VentricularAssistDeviceConstants.SET_CATHETER_DEPTH_CM,
          type: ProcedureConstants.NUMBER_PAD,
          hidden: _hideImpellaFields,
          props: {
            label: t('ventricularAssistDevice.CatheterDepthCm'),
            dataTestId: 'CatheterDepthCm',
            elementId: 'CatheterDepthCm',
            values: catheterDepthCm?.split('') || [],
            onNumPadClick: createNumberPadHandler(setCatheterDepthCm),
            validCharacters: numberPadKeysWithDecimal,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: VentricularAssistDeviceConstants.SET_CATHETER_SERIAL_NUMBER,
          type: ProcedureConstants.NUMBER_PAD,
          hidden: _hideImpellaFields,
          props: {
            label: t('ventricularAssistDevice.CatheterSerialNumber'),
            dataTestId: 'CatheterSerialNumber',
            elementId: 'CatheterSerialNumber',
            values: catheterSerialNumber?.split('') || [],
            onNumPadClick: createNumberPadHandler(setCatheterSerialNumber),
            validCharacters: numberPadKeysWithDecimal,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          type: 'separator',
          hidden: _hideImpellaFields,
          props: {
            heading: t('ventricularAssistDevice.Assessment'),
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: VentricularAssistDeviceConstants.SET_INSERT_LOCATION,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: _hideImpellaFields,
          props: {
            label: t('ventricularAssistDevice.InsertLocation'),
            dataTestID: 'InsertLocation',
            listHeader: t('ventricularAssistDevice.InsertLocation'),
            options: vadInsertLocationOptions,
            action: createChangeHandler(setInsertLocation),
            selectedOption: insertLocation,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: VentricularAssistDeviceConstants.SET_PLACEMENT_LOCATION,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: _hideImpellaFields,
          props: {
            label: t('ventricularAssistDevice.PlacementLocation'),
            dataTestID: 'PlacementLocation',
            listHeader: t('ventricularAssistDevice.PlacementLocation'),
            options: vadPlacementLocationOptions,
            action: createChangeHandler(setPlacementLocation),
            selectedOption: placementLocation,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: VentricularAssistDeviceConstants.SET_SITE_ASSESSMENT,
          type: ProcedureConstants.MULTI_SELECT,
          hidden: _hideImpellaFields,
          props: {
            dataTestID: 'SiteAssessment',
            label: t('ventricularAssistDevice.SiteAssessment'),
            listHeader: t('ventricularAssistDevice.SiteAssessment'),
            options: vadSiteAssessmentOptions,
            action: setSiteAssessment,
            savedValues: siteAssessment,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: VentricularAssistDeviceConstants.SET_LEG_IMMOBILIZED,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: _hideImpellaFields,
          props: {
            label: t('ventricularAssistDevice.LegImmobilized'),
            dataTestID: 'LegImmobilized',
            listHeader: t('ventricularAssistDevice.LegImmobilized'),
            options: yesNoOptions,
            action: createChangeHandler(setLegImmobilized),
            selectedOption: legImmobilized,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: VentricularAssistDeviceConstants.SET_CATHETER_SUTURED_IN_PLACE,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: _hideImpellaFields,
          props: {
            label: t('ventricularAssistDevice.CatheterSuturedInPlace'),
            dataTestID: 'CatheterSuturedInPlace',
            listHeader: t('ventricularAssistDevice.CatheterSuturedInPlace'),
            options: yesNoOptions,
            action: createChangeHandler(setCatheterSuturedInPlace),
            selectedOption: catheterSuturedInPlace,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: VentricularAssistDeviceConstants.SET_DRESSING_CONDITION,
          type: ProcedureConstants.TEXT,
          hidden: _hideImpellaFields,
          props: {
            label: t('ventricularAssistDevice.DressingCondition'),
            dataTestID: 'DressingCondition',
            onChangeAction: createTextHandler(setDressingCondition),
            inputValue: dressingCondition,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: VentricularAssistDeviceConstants.SET_PULSES_DISTAL_TO_INSERTION_SITE,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: _hideImpellaFields,
          props: {
            label: t('ventricularAssistDevice.PulsesDistalToInsertionSite'),
            dataTestID: 'PulsesDistalToInsertionSite',
            listHeader: t('ventricularAssistDevice.PulsesDistalToInsertionSite'),
            options: yesNoOptions,
            action: createChangeHandler(setPulsesDistalToInsertionSite),
            selectedOption: pulsesDistalToInsertionSite,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: VentricularAssistDeviceConstants.SET_ANCHORING_RING_LOCKED,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: _hideImpellaFields,
          props: {
            label: t('ventricularAssistDevice.AnchoringRingLocked'),
            dataTestID: 'AnchoringRingLocked',
            listHeader: t('ventricularAssistDevice.AnchoringRingLocked'),
            options: yesNoOptions,
            action: createChangeHandler(setAnchoringRingLocked),
            selectedOption: anchoringRingLocked,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: VentricularAssistDeviceConstants.SET_HEAD_OF_BED_AT_30_DEGREES,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: _hideImpellaFields,
          props: {
            label: t('ventricularAssistDevice.HeadOfBedAt30Degrees'),
            dataTestID: 'HeadOfBedAt30Degrees',
            listHeader: t('ventricularAssistDevice.HeadOfBedAt30Degrees'),
            options: yesNoOptions,
            action: createChangeHandler(setHeadOfBedAt30Degrees),
            selectedOption: headOfBedAt30Degrees,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: VentricularAssistDeviceConstants.SET_ANTICOAGULATION,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: _hideImpellaFields,
          props: {
            label: t('ventricularAssistDevice.Anticoagulation'),
            dataTestID: 'Anticoagulation',
            listHeader: t('ventricularAssistDevice.Anticoagulation'),
            options: yesNoOptions,
            action: createChangeHandler(setAnticoagulation),
            selectedOption: anticoagulation,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: VentricularAssistDeviceConstants.SET_ANTIDYSRHYTHMIA,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: _hideImpellaFields,
          props: {
            label: t('ventricularAssistDevice.Antidysrhythmia'),
            dataTestID: 'Antidysrhythmia',
            listHeader: t('ventricularAssistDevice.Antidysrhythmia'),
            options: yesNoOptions,
            action: createChangeHandler(setAntidysrhythmia),
            selectedOption: antidysrhythmia,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: VentricularAssistDeviceConstants.SET_PURGE_SOLUTION_DEXTROSE_PERCENT,
          type: ProcedureConstants.NUMBER_PAD,
          hidden: _hideImpellaFields,
          props: {
            label: t('ventricularAssistDevice.PurgeSolutionDextrosePercent'),
            dataTestId: 'PurgeSolutionDextrosePercent',
            elementId: 'PurgeSolutionDextrosePercent',
            values: purgeSolutionDextrosePercent?.split('') || [],
            onNumPadClick: createNumberPadHandler(setPurgeSolutionDextrosePercent),
            validCharacters: numberPadKeysWithDecimal,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: VentricularAssistDeviceConstants.SET_PURGE_SOLUTION_HEPARIN_IU_ML,
          type: ProcedureConstants.NUMBER_PAD,
          hidden: _hideImpellaFields,
          props: {
            label: t('ventricularAssistDevice.PurgeSolutionHeparinIuMl'),
            dataTestId: 'PurgeSolutionHeparinIuMl',
            elementId: 'PurgeSolutionHeparinIuMl',
            values: purgeSolutionHeparinIuMl?.split('') || [],
            onNumPadClick: createNumberPadHandler(setPurgeSolutionHeparinIuMl),
            validCharacters: numberPadKeysWithDecimal,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: VentricularAssistDeviceConstants.SET_PURGE_SOLUTION_FLOW_ML_HR,
          type: ProcedureConstants.NUMBER_PAD,
          hidden: _hideImpellaFields,
          props: {
            label: t('ventricularAssistDevice.PurgeSolutionFlowMlHr'),
            dataTestId: 'PurgeSolutionFlowMlHr',
            elementId: 'PurgeSolutionFlowMlHr',
            values: purgeSolutionFlowMlHr?.split('') || [],
            onNumPadClick: createNumberPadHandler(setPurgeSolutionFlowMlHr),
            validCharacters: numberPadKeysWithDecimal,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: VentricularAssistDeviceConstants.SET_PRESSURE_BAG_USED,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: _hideImpellaFields,
          props: {
            label: t('ventricularAssistDevice.PressureBagUsed'),
            dataTestID: 'PressureBagUsed',
            listHeader: t('ventricularAssistDevice.PressureBagUsed'),
            options: yesNoOptions,
            action: createChangeHandler(setPressureBagUsed),
            selectedOption: pressureBagUsed,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: VentricularAssistDeviceConstants.SET_PURGE_PRESSURE_MMHG,
          type: ProcedureConstants.NUMBER_PAD,
          hidden: _hideImpellaFields,
          props: {
            label: t('ventricularAssistDevice.PurgePressureMmHg'),
            dataTestId: 'PurgePressureMmHg',
            elementId: 'PurgePressureMmHg',
            values: purgePressureMmHg?.split('') || [],
            onNumPadClick: createNumberPadHandler(setPurgePressureMmHg),
            validCharacters: numberPadKeysWithDecimal,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: VentricularAssistDeviceConstants.SET_FLOW_L_MIN,
          type: ProcedureConstants.NUMBER_PAD,
          hidden: _hideImpellaFields,
          props: {
            label: t('ventricularAssistDevice.FlowLMin'),
            dataTestId: 'FlowLMin',
            elementId: 'FlowLMin',
            values: flowLMin?.split('') || [],
            onNumPadClick: createNumberPadHandler(setFlowLMin),
            validCharacters: numberPadKeysWithDecimal,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: VentricularAssistDeviceConstants.SET_POWER_WATTS,
          type: ProcedureConstants.NUMBER_PAD,
          hidden: _hideImpellaFields,
          props: {
            label: t('ventricularAssistDevice.PowerWatts'),
            dataTestId: 'PowerWatts',
            elementId: 'PowerWatts',
            values: powerWatts?.split('') || [],
            onNumPadClick: createNumberPadHandler(setPowerWatts),
            validCharacters: numberPadKeysWithDecimal,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: VentricularAssistDeviceConstants.SET_POWER_TYPE,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: _hideImpellaFields,
          props: {
            label: t('ventricularAssistDevice.PowerType'),
            dataTestID: 'PowerType',
            listHeader: t('ventricularAssistDevice.PowerType'),
            options: vadPowerTypeOptions,
            action: createChangeHandler(setPowerType),
            selectedOption: powerType,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: VentricularAssistDeviceConstants.SET_BATTERY_STATUS,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: _hideImpellaFields,
          props: {
            label: t('ventricularAssistDevice.BatteryStatus'),
            dataTestID: 'BatteryStatus',
            listHeader: t('ventricularAssistDevice.BatteryStatus'),
            options: vadBatteryStatusOptions,
            action: createChangeHandler(setBatteryStatus),
            selectedOption: batteryStatus,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: VentricularAssistDeviceConstants.SET_BACKUP_OPTIONS,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: _hideImpellaFields,
          props: {
            label: t('ventricularAssistDevice.BackupOptions'),
            dataTestID: 'BackupOptions',
            listHeader: t('ventricularAssistDevice.BackupOptions'),
            options: vadBackupOptions,
            action: createChangeHandler(setBackupOptions),
            selectedOption: backupOptions,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: VentricularAssistDeviceConstants.SET_ALARMS,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: _hideImpellaFields,
          props: {
            label: t('ventricularAssistDevice.Alarms'),
            dataTestID: 'Alarms',
            listHeader: t('ventricularAssistDevice.Alarms'),
            options: vadAlarmsOptions,
            action: createChangeHandler(setAlarms),
            selectedOption: alarms,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: VentricularAssistDeviceConstants.SET_VAD_COORDINATOR_NOTIFICATION,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: _hideImpellaFields,
          props: {
            label: t('ventricularAssistDevice.VADCoordinatorNotification'),
            dataTestID: 'VADCoordinatorNotification',
            listHeader: t('ventricularAssistDevice.VADCoordinatorNotification'),
            options: yesNoOptions,
            action: createChangeHandler(setVadCoordinatorNotification),
            selectedOption: vadCoordinatorNotification,
          },
        },
      ],
    },
  ]

  useIonViewWillEnter(() => {
    dispatch(setUpdateInputRows(true))
    return
  })

  if (updateInputRows) {
    dispatch(setInputRows(inputRows))
    dispatch(setUpdateInputRows(false))
  }

  return (
    <IonPage>
      <PCRHeader />
      <IonHeader>
        <IonToolbar color='light'>
          <IonButtons slot='start'>
            <IonButton data-testid='Back' fill='clear' onClick={onBackClick} color='primary-alt'>
              <IonIcon icon={chevronBackOutline} />
              {t('common.buttons.Back')}
            </IonButton>
          </IonButtons>
          <IonButtons slot='end'>
            <IonButton data-testid='Save' fill='clear' onClick={onSaveClick} color='primary-alt'>
              {t('common.buttons.Save')}
            </IonButton>
          </IonButtons>
          <IonTitle>{t('ventricularAssistDevice.VentricularAssistDevice')}</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen color='light'>
        <Procedure
          type={'ventricularAssistDevice'}
          procedureDate={date}
          procedureTime={time}
          assessedBy={assessedBy}
          successfulAttempt={successfulAttempt}
          procedureComplications={procedureComplications}
          response={response}
          responsePNNV={responsePNNV}
          authorization={authorization}
          comments={comments}
          hideAttemptField={true}
          rolePerforming={rolePerforming}
          rolePerformingPNNV={rolePerformingPNNV}
          procedurePerformedByAnotherUnit={procedurePerformedByAnotherUnit}
          procedurePerformedByAnotherUnitPNNV={procedurePerformedByAnotherUnitPNNV}
          pta={pta}
          isEkgImport={isEkgImport}
        />
      </IonContent>
    </IonPage>
  )
}

export default VentricularAssistDevice
